// VIB-950: felles linje-modell for tilbuds- og mersalg-editoren.
//
// Begge editorene opererer på `EditableLine` via delte komponenter. Adapterne
// her mapper toveis mellom de to DB-formene UTEN å endre lagringen:
//   - Tilbud  (`offer_lines`):  unit_price er autoritativ, amount er derivert.
//   - Mersalg (`budget_lines`): amount er autoritativ (external),
//                               quantity_hours × rate (internal hourly).
//
// Kanonisk modell = tilbudsmodellen (unit_price per enhet, amount derivert via
// deriveLineAmount). Mersalg-adapteren syntetiserer unit_price slik at
// deriveLineAmount reproduserer det lagrede amount, og mapper tilbake til
// amount/quantity_hours/estimated_cost ved lagring.

import {
  deriveLineAmount,
  effectiveHoursFromLine,
  BILLABLE_HOURS_PER_DAY,
  type OvertimeSplit,
  type OfferPhase,
} from "./offer-calc";

/** Felles felt-sett editoren opererer på. Phase er string for å romme begge
 *  vokabularene (tilbud: pl/preprod/…; mersalg/budget: pre/prod/post/travel). */
export interface EditableLine {
  id: string;
  description: string;
  phase: string;
  cost_type: string; // "internal" | "external"
  pricing_type: "hourly" | "fixed";
  quantity: number;
  unit: string; // "t" | "d" | "stk" | "fast"
  unit_price: number; // per enhet (kr/t, kr/d, kr/stk)
  purchase_price: number; // innkjøp / estimert kost
  amount: number; // derivert salgssum
  rate_basis_hourly: number | null; // VIB-2097: kr/t-basis, null = katalog
  comment: string | null;
  sort_order: number;
  overtime_split: OvertimeSplit | null; // kun tilbud; mersalg = null
  section_id: string | null; // kun tilbud; mersalg = null
  // Tilbud-metadata som bevares ved round-trip:
  template_id: string | null;
  is_auto_generated: boolean;
  auto_kind: string | null;
}

// ── Tilbud (offer_lines) ─────────────────────────────────────────────

/** Strukturell input — unngår lib→hooks-kobling (felter fra `OfferLine`). */
export interface OfferLineShape {
  id: string;
  phase: OfferPhase;
  description: string;
  cost_type: string;
  pricing_type: "hourly" | "fixed";
  quantity: number;
  unit: string;
  unit_price: number;
  purchase_price: number;
  amount: number;
  rate_basis_hourly?: number | null;
  comment?: string | null;
  sort_order?: number;
  overtime_split?: OvertimeSplit | null;
  section_id?: string | null;
  template_id?: string | null;
  is_auto_generated?: boolean;
  auto_kind?: string | null;
}

export function offerLineToEditable(line: OfferLineShape): EditableLine {
  return {
    id: line.id,
    description: line.description,
    phase: line.phase,
    cost_type: line.cost_type,
    pricing_type: line.pricing_type,
    quantity: line.quantity ?? 0,
    unit: line.unit,
    unit_price: line.unit_price ?? 0,
    purchase_price: line.purchase_price ?? 0,
    amount: line.amount ?? 0,
    rate_basis_hourly: line.rate_basis_hourly ?? null,
    comment: line.comment ?? null,
    sort_order: line.sort_order ?? 0,
    overtime_split: line.overtime_split ?? null,
    section_id: line.section_id ?? null,
    template_id: line.template_id ?? null,
    is_auto_generated: line.is_auto_generated ?? false,
    auto_kind: line.auto_kind ?? null,
  };
}

/** Patch tilbake til `offer_lines`-form. amount re-deriveres kanonisk. */
export function editableToOfferPatch(
  line: EditableLine,
  hourlyRate: number,
): Pick<
  OfferLineShape,
  | "description" | "phase" | "cost_type" | "pricing_type" | "quantity" | "unit"
  | "unit_price" | "purchase_price" | "amount" | "rate_basis_hourly" | "comment"
  | "sort_order" | "overtime_split" | "section_id"
> {
  return {
    description: line.description,
    phase: line.phase as OfferPhase,
    cost_type: line.cost_type,
    pricing_type: line.pricing_type,
    quantity: line.quantity,
    unit: line.unit,
    unit_price: line.unit_price,
    purchase_price: line.purchase_price,
    amount: deriveLineAmount(line, hourlyRate),
    rate_basis_hourly: line.rate_basis_hourly,
    comment: line.comment,
    sort_order: line.sort_order,
    overtime_split: line.overtime_split,
    section_id: line.section_id,
  };
}

// ── Mersalg (budget_lines med upsell_id) ─────────────────────────────

export interface UpsellLineShape {
  id: string;
  phase: string | null;
  description: string | null;
  cost_type: string | null;
  unit: string | null;
  quantity: number | null;
  quantity_hours: number | null;
  amount: number | null;
  estimated_cost: number | null;
  rate_basis_hourly?: number | null;
}

function isInternalHourly(costType: string, unit: string): boolean {
  return costType === "internal" && (unit === "t" || unit === "d");
}

export function upsellLineToEditable(
  line: UpsellLineShape,
  hourlyRate: number,
): EditableLine {
  const cost_type = line.cost_type ?? "external";
  const unit = line.unit ?? "stk";
  const amount = Number(line.amount ?? 0);
  const hourly = isInternalHourly(cost_type, unit);

  let quantity = Number(line.quantity ?? 0);
  let unit_price: number;

  if (hourly) {
    // Rate (kr/t) utledes fra amount/quantity_hours; unit_price per enhet er
    // rate for timer, rate×8 for dager, slik at deriveLineAmount reproduserer
    // amount (qty×unit_price = quantity_hours×rate = amount).
    const qh = Number(line.quantity_hours ?? 0);
    const rate = qh > 0 && amount > 0 ? amount / qh : hourlyRate;
    unit_price = unit === "d" ? rate * BILLABLE_HOURS_PER_DAY : rate;
  } else {
    // External/fixed: amount er autoritativt. Behold quantity der den finnes,
    // ellers behandle som en 1×amount-fastprislinje (bilag med kun beløp).
    if (quantity > 0) {
      unit_price = amount / quantity;
    } else {
      quantity = 1;
      unit_price = amount;
    }
  }

  return {
    id: line.id,
    description: line.description ?? "",
    phase: line.phase ?? "",
    cost_type,
    pricing_type: hourly ? "hourly" : "fixed",
    quantity,
    unit,
    unit_price,
    purchase_price: Number(line.estimated_cost ?? 0),
    amount,
    rate_basis_hourly: line.rate_basis_hourly ?? null,
    comment: null,
    sort_order: 0,
    overtime_split: null, // mersalg har ikke overtid
    section_id: null, // mersalg er kun fase-gruppert
    template_id: null,
    is_auto_generated: false,
    auto_kind: null,
  };
}

/** Patch tilbake til mersalg-form (`budget_lines`). amount re-deriveres
 *  kanonisk; quantity_hours/estimated_cost utledes. */
export function editableToUpsellPatch(
  line: EditableLine,
  hourlyRate: number,
): {
  description: string;
  phase: string;
  cost_type: string;
  unit: string;
  quantity: number;
  quantity_hours: number;
  amount: number;
  estimated_cost: number;
  rate_basis_hourly: number | null;
} {
  const hourly = isInternalHourly(line.cost_type, line.unit);
  return {
    description: line.description,
    phase: line.phase,
    cost_type: line.cost_type,
    unit: line.unit,
    quantity: line.quantity,
    quantity_hours: hourly ? effectiveHoursFromLine(line) : 0,
    amount: deriveLineAmount(line, hourlyRate),
    estimated_cost: line.purchase_price,
    rate_basis_hourly: line.rate_basis_hourly,
  };
}

/**
 * VIB-2097: katalogens `default_rate` er pris PER `default_unit` (kr/t for
 * «t», kr/d for «d»). Mersalg-adapteren og `deriveLineAmount`-fallbacken
 * regner derimot alltid i kr/t og ganger selv opp med 8 for dag-linjer, så
 * en dag-mal MÅ normaliseres til timesats før den brukes som fallback.
 * Uten dette fikk en ny «Videofotograf»-linje (12 000/d) sats 96 000/d.
 */
export function templateHourlyRate(template: {
  default_rate: number | null | undefined;
  default_unit: string | null | undefined;
}): number {
  const rate = Number(template.default_rate) || 0;
  if (rate <= 0) return 0;
  const unit = template.default_unit === "dag" ? "d" : template.default_unit;
  return unit === "d" ? rate / BILLABLE_HOURS_PER_DAY : rate;
}

/**
 * Timesats (kr/t) per mal-id, normalisert via `templateHourlyRate`. Brukes
 * som `rateByTemplate` i både tilbuds- og mersalg-editoren.
 */
export function buildTemplateHourlyRates(
  templates: ReadonlyArray<{
    id: string;
    default_rate: number | null | undefined;
    default_unit: string | null | undefined;
  }>,
): Map<string, number> {
  return new Map(templates.map((t) => [t.id, templateHourlyRate(t)]));
}

/**
 * VIB-1210/VIB-2097: katalogens timesats for en linje. Katalog-linjer (med
 * `template_id`) bruker malens sats, fritekst-linjer (uten mal) og maler uten
 * gyldig timesats bruker system-`hourly_rate`. `rateByTemplate` MÅ inneholde
 * timesatser (se `buildTemplateHourlyRates`). Brukes som fallback når en
 * linje mangler sats, og som tidsbasis når en fast/stk-linje byttes til t/d.
 */
export function resolveLineRate(
  templateId: string | null | undefined,
  rateByTemplate: ReadonlyMap<string, number>,
  hourlyRate: number,
): number {
  const r = templateId ? rateByTemplate.get(templateId) : undefined;
  return r != null && r > 0 ? r : hourlyRate;
}

/** Mersalg-navnet fra VIB-1210. Samme regel som `resolveLineRate`. */
export const resolveUpsellLineRate = resolveLineRate;
